import { pxToRem } from '../../design-tokens/style-utilities';
import { ANWBBrandRefresh } from '../../design-tokens/theme';
/**
 * This will detect the correct environment
 */
import { getLocalStorage, setLocalStorage } from './storage';
export const getEnvironment = () => {
    var _a;
    const NEXT_PUBLIC_PONCHO_ENV = (typeof process !== 'undefined' &&
        ((_a = process === null || process === void 0 ? void 0 : process.env) === null || _a === void 0 ? void 0 : _a.NEXT_PUBLIC_PONCHO_ENV));
    if (NEXT_PUBLIC_PONCHO_ENV) {
        return NEXT_PUBLIC_PONCHO_ENV;
    }
    if (typeof window === 'undefined') {
        return 'prod';
    }
    switch (true) {
        case window.location.hostname.indexOf('ontw') > -1:
            return 'ontw';
        case window.location.hostname.indexOf('127.0.0.1') > -1:
        case window.location.hostname.indexOf('test') > -1:
        case window.location.hostname.indexOf('local') > -1:
        case window.location.hostname.indexOf('localhost') > -1:
        case window.location.hostname === 'gitlab.anwbonline.nl':
            return 'test';
        case window.location.hostname.indexOf('acc') > -1:
        case window.location.hostname === 'poncho.services.anwb.nl':
            return 'acc';
        // The default is production
        default:
            return 'prod';
    }
};
/**
 * This variable contains the host environment
 */
export const environment = getEnvironment();
export const webUrls = {
    ontw: 'www-ontw.anwb.nl',
    test: 'www-test.anwb.nl',
    acc: 'www-acc.anwb.nl',
    prod: 'www.anwb.nl',
};
/**
 * This will return the web url for the current environment
 */
export const getWebUrl = () => typeof window === 'undefined' ? webUrls.ontw : webUrls[environment];
/**
 * This will return the static assets url for the current environment
 */
export const getStaticUrl = () => ({
    ontw: 'static-ontw.anwb.nl',
    test: 'static-test.anwb.nl',
    acc: 'static-acc.anwb.nl',
    prod: 'static.anwb.nl',
})[environment];
/**
 *
 * @return Array
 */
export const getUrlParameters = (type) => {
    var _a, _b;
    let parametersString;
    const parameters = [];
    if (!type || type === '?') {
        // ? query
        parametersString = (_a = window.location.search) === null || _a === void 0 ? void 0 : _a.substr(1);
    }
    else {
        // # hash
        parametersString = (_b = window.location.hash) === null || _b === void 0 ? void 0 : _b.substr(1);
    }
    if (!parametersString) {
        return parameters;
    }
    const keyValues = parametersString.split('&');
    keyValues.forEach((keyValue) => {
        const [key, val] = keyValue.split('=');
        parameters.push({ name: key, value: decodeURIComponent(val) });
    });
    return parameters;
};
/**
 *
 * @return String
 */
export const getUrlParameter = (name = '', type) => {
    let value;
    getUrlParameters(type).some((parameter) => {
        if (name === parameter.name) {
            value = parameter.value;
            return true;
        }
        return false;
    });
    return value;
};
/**
 * This will check whether the user is visiting from a mobile device
 * This only supports iPhone, iPad, iPod, Android and Windows Phone
 * @return A boolean indicating whether the user has a mobile device
 */
export const checkMobileOperatingSystem = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return /iPhone|iPad|iPod|Android|Windows Phone/i.test(userAgent);
};
/**
 * This will test what mobile operating system the user is on
 * @return A string representing the operating system
 */
export const getMobileOperatingSystem = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/windows phone/i.test(userAgent)) {
        return 'windows';
    }
    if (/android/i.test(userAgent)) {
        return 'android';
    }
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
        return 'ios';
    }
    return 'unknown';
};
/**
 * This will check if the users browser has support for the requested input type
 * @param  type The type of input to test
 * @return      Whether the browser has support
 */
export const checkInputSupport = (type) => {
    const input = document.createElement('input');
    input.setAttribute('type', type);
    return input.type === type;
};
export const getApplicationSizeMediaQueryLists = (() => {
    let cache;
    return ({ theme = ANWBBrandRefresh, }) => {
        if (cache === undefined) {
            cache = [
                {
                    size: 'small',
                    mediaQueryList: window.matchMedia(`(max-width: ${pxToRem(theme.viewportBreakpoint.sm)})`),
                },
                {
                    size: 'medium',
                    mediaQueryList: window.matchMedia(`(min-width: ${pxToRem(theme.viewportBreakpoint.sm)}) and (max-width: ${pxToRem(theme.viewportBreakpoint.md)})`),
                },
                {
                    size: 'large',
                    mediaQueryList: window.matchMedia(`(min-width: ${pxToRem(theme.viewportBreakpoint.md)})`),
                },
            ];
        }
        return cache;
    };
})();
export const getApplicationSize = ({ theme = ANWBBrandRefresh, }) => {
    var _a;
    if (typeof window === 'undefined' || typeof document === 'undefined')
        return 'small';
    const applicationSize = getApplicationSizeMediaQueryLists({ theme }).find(({ mediaQueryList }) => mediaQueryList.matches);
    return (_a = applicationSize === null || applicationSize === void 0 ? void 0 : applicationSize.size) !== null && _a !== void 0 ? _a : 'small';
};
/**
 * This will return whether the document is hidden or not
 * @return This will return a boolean indicating whether the document is currently hidden
 */
export const isDocumentHidden = () => {
    return document.visibilityState === 'hidden';
};
export const getHiddenPropertyName = () => {
    if (typeof document.hidden !== 'undefined') {
        return 'hidden';
    }
    if (typeof document.msHidden !== 'undefined') {
        return 'msHidden';
    }
    if (typeof document.webkitHidden !== 'undefined') {
        return 'webkitHidden';
    }
    return undefined;
};
/**
 * This will check whether the browser has support for visibilityChange and if so under which name
 */
export const getVisibilityChangePropertyName = () => {
    if (typeof document.hidden !== 'undefined') {
        return 'visibilitychange';
    }
    if (typeof document.msHidden !== 'undefined') {
        return 'msvisibilitychange';
    }
    if (typeof document.webkitHidden !== 'undefined') {
        return 'webkitvisibilitychange';
    }
    return undefined;
};
const TRACKING_CONSENT_KEY = 'tracking-consent-v20';
/**
 * This will check the tracking consent level given by user
 */
export const getTrackingConsentLevel = () => {
    return getLocalStorage(TRACKING_CONSENT_KEY, 0);
};
export const setTrackingConsentLevel = (value = 3) => {
    const validNumber = Number(value) || 0;
    const biggerThan = Math.max(0, validNumber);
    const numberWithinRange = Math.min(3, biggerThan);
    setLocalStorage(TRACKING_CONSENT_KEY, numberWithinRange);
    return numberWithinRange;
};
